"use client"

import type { FlagComponent } from "country-flag-icons/react/3x2"
import AE from "country-flag-icons/react/3x2/AE"
import AR from "country-flag-icons/react/3x2/AR"
import AT from "country-flag-icons/react/3x2/AT"
import AU from "country-flag-icons/react/3x2/AU"
import BE from "country-flag-icons/react/3x2/BE"
import BR from "country-flag-icons/react/3x2/BR"
import CA from "country-flag-icons/react/3x2/CA"
import CH from "country-flag-icons/react/3x2/CH"
import CL from "country-flag-icons/react/3x2/CL"
import CN from "country-flag-icons/react/3x2/CN"
import CO from "country-flag-icons/react/3x2/CO"
import CR from "country-flag-icons/react/3x2/CR"
import CZ from "country-flag-icons/react/3x2/CZ"
import DE from "country-flag-icons/react/3x2/DE"
import DK from "country-flag-icons/react/3x2/DK"
import DO from "country-flag-icons/react/3x2/DO"
import EC from "country-flag-icons/react/3x2/EC"
import EG from "country-flag-icons/react/3x2/EG"
import ES from "country-flag-icons/react/3x2/ES"
import FR from "country-flag-icons/react/3x2/FR"
import GB from "country-flag-icons/react/3x2/GB"
import GR from "country-flag-icons/react/3x2/GR"
import GT from "country-flag-icons/react/3x2/GT"
import HK from "country-flag-icons/react/3x2/HK"
import HR from "country-flag-icons/react/3x2/HR"
import ID from "country-flag-icons/react/3x2/ID"
import IE from "country-flag-icons/react/3x2/IE"
import IL from "country-flag-icons/react/3x2/IL"
import IN from "country-flag-icons/react/3x2/IN"
import IT from "country-flag-icons/react/3x2/IT"
import JM from "country-flag-icons/react/3x2/JM"
import JP from "country-flag-icons/react/3x2/JP"
import KE from "country-flag-icons/react/3x2/KE"
import KR from "country-flag-icons/react/3x2/KR"
import MA from "country-flag-icons/react/3x2/MA"
import MX from "country-flag-icons/react/3x2/MX"
import MY from "country-flag-icons/react/3x2/MY"
import NG from "country-flag-icons/react/3x2/NG"
import NL from "country-flag-icons/react/3x2/NL"
import NO from "country-flag-icons/react/3x2/NO"
import NZ from "country-flag-icons/react/3x2/NZ"
import PA from "country-flag-icons/react/3x2/PA"
import PE from "country-flag-icons/react/3x2/PE"
import PH from "country-flag-icons/react/3x2/PH"
import PL from "country-flag-icons/react/3x2/PL"
import PR from "country-flag-icons/react/3x2/PR"
import PT from "country-flag-icons/react/3x2/PT"
import RU from "country-flag-icons/react/3x2/RU"
import SA from "country-flag-icons/react/3x2/SA"
import SE from "country-flag-icons/react/3x2/SE"
import SG from "country-flag-icons/react/3x2/SG"
import TH from "country-flag-icons/react/3x2/TH"
import TR from "country-flag-icons/react/3x2/TR"
import TW from "country-flag-icons/react/3x2/TW"
import US from "country-flag-icons/react/3x2/US"
import UY from "country-flag-icons/react/3x2/UY"
import VN from "country-flag-icons/react/3x2/VN"
import ZA from "country-flag-icons/react/3x2/ZA"

/**
 * Country flag as a real SVG, not an emoji.
 *
 * Emoji flags are rendered by the operating system, so the same filter bar
 * looks different on every machine — Apple's glossy ovals, Windows' two-letter
 * fallback boxes (Windows ships no flag glyphs at all), Android's flat rects.
 * A 3x2 SVG with a small radius is the same everywhere, sits on the pixel grid
 * at 16px, and matches how Vercel and Stripe draw them.
 *
 * Imports are static and per-country on purpose: the package ships ~250 flags
 * and only the roster reaches the bundle. This list MUST cover every code in
 * COUNTRY_ROSTER — a missing one renders nothing at all rather than erroring,
 * which is exactly how two dozen countries silently vanished from the filter
 * row while the data behind it was correct.
 */
const FLAGS: Record<string, FlagComponent> = {
  AE, AR, AT, AU, BE, BR, CA, CH,
  CL, CN, CO, CR, CZ, DE, DK, DO,
  EC, EG, ES, FR, GB, GR, GT, HK,
  HR, ID, IE, IL, IN, IT, JM, JP,
  KE, KR, MA, MX, MY, NG, NL, NO,
  NZ, PA, PE, PH, PL, PR, PT, RU,
  SA, SE, SG, TH, TR, TW, US, UY,
  VN, ZA,
}

export function CountryFlag({
  code,
  className = "w-4 h-auto",
}: {
  code: string
  className?: string
}) {
  const Flag = FLAGS[code]
  if (!Flag) return null
  return (
    <Flag
      // rounded-[2px] + the hairline ring keeps a white flag (JP) from
      // dissolving into a light background, and stops any of them reading as
      // a raw image dropped into the row.
      className={`${className} rounded-[2px] ring-1 ring-inset ring-black/10 dark:ring-white/15`}
      aria-hidden
    />
  )
}
